/**
 * Main-thread RPC client for onnxInference.worker.js.
 *
 * Each method returns a Promise that resolves when the worker replies with
 * the matching id, or rejects on ERROR or worker crash.
 *
 * Singleton usage (recommended):
 *   import { getOnnxWorkerClient } from './onnxWorkerClient';
 *   const client = getOnnxWorkerClient();
 */

let _seq = 0;
const _nextId = () => ++_seq;

/**
 * A frozen/killed worker (e.g. iOS Safari killing it under memory pressure, without firing
 * any `error` event) never replies and never triggers onerror — this main-thread timer is
 * the only thing that can still notice, since it keeps running independently of the worker.
 */
const OP_WATCHDOG_MS = 30000;

export class OnnxWorkerClient {
  constructor() {
    this._worker = new Worker(new URL('./onnxInference.worker.js', import.meta.url));
    /** @type {Map<number, { resolve: Function, reject: Function }>} */
    this._pending = new Map();
    this._worker.onmessage = this._onMessage.bind(this);
    this._worker.onerror   = this._onError.bind(this);
  }

  _onMessage(ev) {
    const msg = ev.data || {};
    const rec = this._pending.get(msg.id);
    if (!rec) return;
    this._pending.delete(msg.id);
    if (msg.type === 'ERROR') {
      rec.reject(new Error(msg.message || 'OnnxWorker error'));
    } else {
      rec.resolve(msg);
    }
  }

  _onError(err) {
    const message = err.message || 'OnnxWorker script error';
    console.error('[onnx-rpc] worker error event', message);
    for (const rec of this._pending.values()) rec.reject(new Error(message));
    this._pending.clear();
  }

  _send(type, payload = {}, transferables = []) {
    const id = _nextId();
    const label = payload.kind ? `${type}(${payload.kind})` : type;
    return new Promise((resolve, reject) => {
      const watchdogId = setTimeout(() => {
        console.error(`[onnx-rpc] main-thread watchdog: ${label} (id=${id}) got no reply within ${OP_WATCHDOG_MS}ms — worker likely killed/frozen by the browser. Terminating.`);
        this._onError(new Error(`ONNX model worker stopped responding during ${label} — the browser may have closed it to free up memory. Please retry.`));
        try { this._worker.terminate(); } catch (_) {}
        if (_singleton === this) _singleton = null; // force a fresh worker on the next getOnnxWorkerClient()
      }, OP_WATCHDOG_MS);

      this._pending.set(id, {
        resolve: (msg) => { clearTimeout(watchdogId); resolve(msg); },
        reject: (err) => { clearTimeout(watchdogId); reject(err); },
      });
      this._worker.postMessage({ type, id, ...payload }, transferables);
    });
  }

  // ── Public API ───────────────────────────────────────────────────────────

  ping() {
    return this._send('PING');
  }

  /**
   * Load an ONNX model into the worker (transfers the buffer — zero copy).
   * @param {'drdme'|'fundus'|'eyes'} kind
   * @param {ArrayBuffer} buffer
   * @param {boolean} [isMobile]
   */
  async loadModel(kind, buffer, isMobile = false) {
    const transferable = buffer instanceof ArrayBuffer ? buffer : buffer.buffer;
    return this._send('LOAD_MODEL', { kind, buffer: transferable, isMobile }, [transferable]);
  }

  /**
   * Send Mahalanobis mean + covinv (transfers both buffers — zero copy).
   * @param {ArrayBuffer} meanBuffer   Float32 [1280]
   * @param {ArrayBuffer} covinvBuffer Float32 [1280×1280]
   */
  async loadMahalFiles(meanBuffer, covinvBuffer) {
    const mb = meanBuffer   instanceof ArrayBuffer ? meanBuffer   : meanBuffer.buffer;
    const cb = covinvBuffer instanceof ArrayBuffer ? covinvBuffer : covinvBuffer.buffer;
    return this._send('LOAD_MAHAL', { mean: mb, covinv: cb }, [mb, cb]);
  }

  /**
   * Cache a preprocessed tensor in the worker for subsequent RUN_MODEL calls.
   * Transfers the underlying ArrayBuffer (main-thread view is detached).
   * @param {string} tensorId
   * @param {Float32Array} normalizedData NCHW [3×512×512]
   */
  cacheTensor(tensorId, normalizedData) {
    const buffer = normalizedData.buffer.slice(
      normalizedData.byteOffset,
      normalizedData.byteOffset + normalizedData.byteLength,
    );
    return this._send('CACHE_TENSOR', { tensorId, buffer }, [buffer]);
  }

  /**
   * Run one model on a previously cached tensor (by tensorId).
   * @param {'drdme'|'fundus'|'eyes'} kind
   * @param {string} tensorId
   * @returns {Promise<{ kind, result }>}
   */
  runModel(kind, tensorId) {
    return this._send('RUN_MODEL', { kind, tensorId });
  }

  /**
   * Free the cached tensor for tensorId.
   */
  clearTensor(tensorId) {
    return this._send('CLEAR_TENSOR', { tensorId });
  }

  /**
   * Release a model session in the worker (frees WASM heap for that model).
   * @param {'drdme'|'fundus'|'eyes'} kind
   */
  disposeModel(kind) {
    return this._send('DISPOSE_MODEL', { kind });
  }

  /** Hard-terminate the worker (app teardown only). */
  terminate() {
    this._worker.terminate();
  }
}

// ---------------------------------------------------------------------------
// Singleton — one worker per app lifecycle
// ---------------------------------------------------------------------------

/** @type {OnnxWorkerClient|null} */
let _singleton = null;

export function getOnnxWorkerClient() {
  if (!_singleton) {
    _singleton = new OnnxWorkerClient();
  }
  return _singleton;
}

/**
 * Terminate and discard the singleton worker, forcing a fresh one on the next call to
 * getOnnxWorkerClient(). Call this after each patient's full screening is done.
 *
 * WHY: WASM heap never shrinks back to the OS after session.release() — it stays at
 * high-water mark for the worker's lifetime. After processing many images across a
 * screening session, the cumulative resident memory grows until iOS jetsam kills the
 * tab when the app is backgrounded. A fresh worker starts with a zero heap.
 */
export function resetOnnxWorkerClient() {
  if (_singleton) {
    try { _singleton.terminate(); } catch (_) {}
    _singleton = null;
  }
}
